import { Box, Stack } from "@repo/ui/core";
import { motion } from "framer-motion";
import { SlideTitle } from "@components/SlideTitle";

type Phase = {
  id: string;
  label: string;
  duration: string;
  color: string;
  items: string[];
  position: number; // 0-based index for layout
};

const PHASES: Phase[] = [
  {
    id: "foundation",
    label: "Foundation",
    duration: "Month 1",
    color: "#4ade80",
    items: [
      "Define success criteria & KPIs",
      "Provision cloud infrastructure",
      "Configure GitLab integration",
      "Onboard initial 10 engineers",
      "First AAOS build pipeline",
    ],
    position: 0,
  },
  {
    id: "scale",
    label: "Scale & Validate",
    duration: "Month 2",
    color: "#22d3ee",
    items: [
      "Expand to 50 engineers",
      "CI/CD pipeline validation",
      "Emulator test execution at scale",
      "Developer workflow optimization",
      "Snapshot & template baselines",
    ],
    position: 1,
  },
  {
    id: "evaluate",
    label: "Evaluate & Decide",
    duration: "Month 3",
    color: "#a78bfa",
    items: [
      "Full 100-engineer deployment",
      "KPI measurement & reporting",
      "Stakeholder review sessions",
      "Production readiness assessment",
      "Pilot → Production decision",
    ],
    position: 2,
  },
];

const PROVIDES = {
  null: ["Cloud infrastructure", "Platform access & support", "Dedicated engineer", "Weekly syncs"],
  customer: ["AAOS source / config", "Engineering team access", "GitLab instance access", "Success criteria"],
};

export function POCProposalSlide() {
  const svgW = 900;
  const svgH = 440;
  const timelineY = 50;
  const phaseH = 150;
  const phaseW = 240;
  const phaseGap = 30;
  const totalW = phaseW * 3 + phaseGap * 2;
  const startX = (svgW - totalW) / 2;

  const providesY = timelineY + phaseH + 60;
  const providesW = 360;
  const providesGap = 60;
  const providesStartX = (svgW - providesW * 2 - providesGap) / 2;

  // Engineer ramp indicator
  const rampY = timelineY + phaseH + 20;
  const rampLabels = ["10", "50", "100"];

  return (
    <Box w="100%" maw={1400} px="xl">
      <Stack gap="md">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <SlideTitle
            title="Pilot Proposal"
            subtitle="3-month pilot — 100 engineers — from foundation to production decision"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.4 }}
        >
          <svg
            viewBox={`0 0 ${svgW} ${svgH}`}
            width="100%"
            style={{ display: "block", maxHeight: "62vh" }}
            aria-label="Pilot proposal timeline showing 3 phases over 3 months targeting 100 engineers"
          >
            {/* Timeline connector */}
            <motion.line
              x1={startX + phaseW / 2}
              y1={timelineY + phaseH + 8}
              x2={startX + (phaseW + phaseGap) * 2 + phaseW / 2}
              y2={timelineY + phaseH + 8}
              stroke="rgba(255,255,255,0.06)"
              strokeWidth={2}
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ delay: 0.5, duration: 0.8 }}
            />

            {/* Phase cards */}
            {PHASES.map((phase, i) => {
              const x = startX + i * (phaseW + phaseGap);
              return (
                <motion.g
                  key={phase.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + i * 0.15, duration: 0.4 }}
                >
                  {/* Card bg */}
                  <rect
                    x={x}
                    y={timelineY}
                    width={phaseW}
                    height={phaseH}
                    rx={10}
                    fill="rgba(255,255,255,0.025)"
                    stroke={phase.color}
                    strokeWidth={1.5}
                    strokeOpacity={0.2}
                  />

                  {/* Top accent */}
                  <rect x={x} y={timelineY} width={phaseW} height={3} rx={1.5} fill={phase.color} opacity={0.6} />

                  {/* Phase number on timeline */}
                  <circle cx={x + phaseW / 2} cy={timelineY + phaseH + 8} r={10} fill="rgba(10,10,14,0.9)" stroke={phase.color} strokeWidth={2} />
                  <text
                    x={x + phaseW / 2}
                    y={timelineY + phaseH + 12}
                    textAnchor="middle"
                    fontSize={10}
                    fill={phase.color}
                    fontWeight={700}
                    fontFamily="inherit"
                  >
                    {i + 1}
                  </text>

                  {/* Duration badge */}
                  <rect x={x + phaseW - 68} y={timelineY + 8} width={58} height={18} rx={9} fill={phase.color} opacity={0.12} />
                  <text
                    x={x + phaseW - 39}
                    y={timelineY + 21}
                    textAnchor="middle"
                    fontSize={9}
                    fill={phase.color}
                    fontWeight={600}
                    fontFamily="inherit"
                  >
                    {phase.duration}
                  </text>

                  {/* Phase label */}
                  <text x={x + 14} y={timelineY + 24} fontSize={13} fill={phase.color} fontWeight={700} fontFamily="inherit">
                    {phase.label}
                  </text>

                  {/* Items */}
                  {phase.items.map((item, j) => (
                    <g key={item}>
                      <circle cx={x + 20} cy={timelineY + 42 + j * 20} r={2.5} fill={phase.color} opacity={0.5} />
                      <text
                        x={x + 30}
                        y={timelineY + 46 + j * 20}
                        fontSize={10}
                        fill="rgba(255,255,255,0.6)"
                        fontFamily="inherit"
                      >
                        {item}
                      </text>
                    </g>
                  ))}

                  {/* Engineer count under timeline dot */}
                  <text
                    x={x + phaseW / 2}
                    y={rampY + 10}
                    textAnchor="middle"
                    fontSize={9}
                    fill={phase.color}
                    fontWeight={600}
                    fontFamily="inherit"
                    opacity={0.7}
                  >
                    {rampLabels[i]} engineers
                  </text>
                </motion.g>
              );
            })}

            {/* Provides section */}
            {[
              { key: "null", label: "Null Autos Provides", items: PROVIDES.null, color: "var(--customer-accent-light)", borderColor: "var(--customer-accent)" },
              { key: "customer", label: "Customer Provides", items: PROVIDES.customer, color: "#818cf8", borderColor: "#818cf8" },
            ].map((section, si) => {
              const x = providesStartX + si * (providesW + providesGap);
              return (
                <motion.g
                  key={section.key}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.2 + si * 0.15, duration: 0.4 }}
                >
                  <rect
                    x={x}
                    y={providesY}
                    width={providesW}
                    height={100}
                    rx={8}
                    fill="rgba(255,255,255,0.02)"
                    stroke={section.borderColor}
                    strokeWidth={1}
                    strokeOpacity={0.15}
                  />
                  <text
                    x={x + 14}
                    y={providesY + 20}
                    fontSize={11}
                    fill={section.color}
                    fontWeight={700}
                    fontFamily="inherit"
                  >
                    {section.label}
                  </text>
                  {section.items.map((item, j) => (
                    <text
                      key={item}
                      x={x + 24}
                      y={providesY + 40 + j * 16}
                      fontSize={10}
                      fill="rgba(255,255,255,0.5)"
                      fontFamily="inherit"
                    >
                      • {item}
                    </text>
                  ))}
                </motion.g>
              );
            })}
          </svg>
        </motion.div>
      </Stack>
    </Box>
  );
}
